import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';
import * as yup from 'yup';

import RoomRatesTableRateEditorForm from 'components/RoomRatesTableRateEditorForm';

import { textFieldValue, useErrorHandler } from 'lib';
import { useDialog } from 'components/Dialog';
import { useCurrentUser } from 'components/CurrentUserProvider';

import {
  RoomRatesTableRateEditor_rateByRoom$data,
  RoomRatesTableRateEditor_rateByRoom$key,
} from '__generated__/RoomRatesTableRateEditor_rateByRoom.graphql';
import { RoomRatesTableRateEditor_package$key } from '__generated__/RoomRatesTableRateEditor_package.graphql';
import {
  RoomRatesTableRateEditor_packageDate$data,
  RoomRatesTableRateEditor_packageDate$key,
} from '__generated__/RoomRatesTableRateEditor_packageDate.graphql';
import {
  RoomRatesTableRateEditor_cateringOption$data,
  RoomRatesTableRateEditor_cateringOption$key,
} from '__generated__/RoomRatesTableRateEditor_cateringOption.graphql';
import { RoomRatesTableRateEditorStoreMutation } from '__generated__/RoomRatesTableRateEditorStoreMutation.graphql';
import { RoomRatesTableRateEditorDeleteMutation } from '__generated__/RoomRatesTableRateEditorDeleteMutation.graphql';
import { RoomRatesTableRateEditor_data$key } from '__generated__/RoomRatesTableRateEditor_data.graphql';

export type FormFields = {
  priceNett: string;
  roomPriceAdult: string;
  roomPriceChild: string;
};

type Props = {
  data: RoomRatesTableRateEditor_data$key;
  rateByRoom: RoomRatesTableRateEditor_rateByRoom$key;
  cateringOption: RoomRatesTableRateEditor_cateringOption$key;
  package: RoomRatesTableRateEditor_package$key;
  packageDate: RoomRatesTableRateEditor_packageDate$key;
};

const schema = yup.object({
  roomPriceAdult: yup.number().nullable().required('Privaloma'),
  roomPriceChild: yup.number().nullable().required('Privaloma'),
});

const makeVariables = (
  packageDate: RoomRatesTableRateEditor_packageDate$data,
  rateByRoom: RoomRatesTableRateEditor_rateByRoom$data,
  cateringOption: RoomRatesTableRateEditor_cateringOption$data,
  values: FormFields
) => ({
  id: packageDate.id,
  room: { id: rateByRoom.room.id },
  cateringOption: { id: cateringOption.id },
  input: {
    priceNett: values.priceNett,
    roomPriceAdult: Number(values.roomPriceAdult),
    roomPriceChild: Number(values.roomPriceChild),
  },
});

const RoomRatesTableRateEditor: FC<Props> = ({ ...props }) => {
  const { close } = useDialog();
  const errorHandler = useErrorHandler();
  const { itemsPerPage } = useCurrentUser();

  const data = useFragment(
    graphql`
      fragment RoomRatesTableRateEditor_data on Query {
        ...RoomRatesTableRateEditorForm_data
      }
    `,
    props.data
  );

  const rateByRoom = useFragment(
    graphql`
      fragment RoomRatesTableRateEditor_rateByRoom on RateByRoom {
        __id

        room {
          id
        }

        roomRate {
          id
          priceNett
          roomPriceAdult
          roomPriceChild
        }

        ...RoomRatesTableRateEditorForm_rateByRoom
      }
    `,
    props.rateByRoom
  );

  const cateringOption = useFragment(
    graphql`
      fragment RoomRatesTableRateEditor_cateringOption on CateringOption {
        id

        ...RoomRatesTableRateEditorForm_cateringOption
      }
    `,
    props.cateringOption
  );

  const pack = useFragment(
    graphql`
      fragment RoomRatesTableRateEditor_package on Package {
        ...RoomRatesTableRateEditorForm_package
      }
    `,
    props.package
  );

  const packageDate = useFragment(
    graphql`
      fragment RoomRatesTableRateEditor_packageDate on PackageDate {
        id
        ...RoomRatesTableRateEditorForm_packageDate
      }
    `,
    props.packageDate
  );

  const { __id, roomRate } = rateByRoom;

  const [store, storing] =
    useMutation<RoomRatesTableRateEditorStoreMutation>(graphql`
      mutation RoomRatesTableRateEditorStoreMutation(
        $id: ID!
        $room: SetEntityInput!
        $cateringOption: SetEntityInput!
        $input: StoreRoomRateInput!
        $first: Int!
      ) {
        storeRoomRate(
          id: $id
          room: $room
          cateringOption: $cateringOption
          input: $input
        ) {
          ...PackageEditTabs_package
          ...PackageSidebar_package
        }
      }
    `);

  const [remove, deleting] =
    useMutation<RoomRatesTableRateEditorDeleteMutation>(graphql`
      mutation RoomRatesTableRateEditorDeleteMutation($first: Int!, $id: ID!) {
        deleteRoomRate(id: $id) {
          ...PackageEditTabs_package
          ...PackageSidebar_package
        }
      }
    `);

  const initial: FormFields = {
    priceNett: roomRate?.priceNett ?? '',
    roomPriceAdult: textFieldValue(roomRate?.roomPriceAdult),
    roomPriceChild: textFieldValue(roomRate?.roomPriceChild),
  };

  const handleSubmit = useCallback(
    (values: FormFields) => {
      store({
        variables: {
          first: itemsPerPage,
          ...makeVariables(packageDate, rateByRoom, cateringOption, values),
        },

        ...errorHandler({
          onCompleted: close,
        }),
      });
    },
    [
      cateringOption,
      close,
      errorHandler,
      itemsPerPage,
      packageDate,
      rateByRoom,
      store,
    ]
  );

  const handleDelete = useCallback(() => {
    if (!roomRate) return;

    remove({
      variables: {
        id: roomRate.id,
        first: itemsPerPage,
      },
      ...errorHandler(),

      optimisticUpdater: store => {
        store.get(__id)?.setValue(null, 'roomRate');
      },
    });

    close();
  }, [__id, close, errorHandler, itemsPerPage, remove, roomRate]);

  return (
    <Formik
      initialValues={initial}
      onSubmit={handleSubmit}
      validationSchema={schema}>
      <RoomRatesTableRateEditorForm
        data={data}
        rateByRoom={rateByRoom}
        cateringOption={cateringOption}
        package={pack}
        packageDate={packageDate}
        storing={storing}
        deleting={deleting}
        onDelete={handleDelete}
      />
    </Formik>
  );
};

export default RoomRatesTableRateEditor;
